import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Building2, Download, Search, ExternalLink, Shield, History, Star, Clock, Activity } from 'lucide-react';
import { downloadCsv, rowsToCsv } from '@/lib/csvExport';

interface AgencyMetrics {
  id: string;
  name: string;
  slug: string | null;
  city: string | null;
  isVerified: boolean;
  logoUrl: string | null;
  ownerId: string | null;
  active: number;
  draft: number;
  pending: number;
  rejected90d: number;
  leads30d: number;
  leads90d: number;
  wonLeads90d: number;
  conversionPct: number;
  featuredActive: number;
  totalSpend: number;
  compliance: { logo: boolean; verified: boolean; agentsComplete: boolean };
  // New metrics
  avgResponseMinutes: number | null;     // null = no data
  responseSampleSize: number;
  unansweredCount: number;
  reviewAvg: number | null;              // null = no reviews
  reviewCount: number;
  lastActivityAt: string | null;         // ISO
  lastActivityType: 'listing' | 'lead' | 'lead_note' | null;
}

function complianceScore(m: AgencyMetrics) {
  const c = m.compliance;
  return [c.logo, c.verified, c.agentsComplete].filter(Boolean).length;
}

function tone(score: number, total = 3): 'ok' | 'warn' | 'bad' {
  const pct = score / total;
  if (pct >= 0.8) return 'ok';
  if (pct >= 0.5) return 'warn';
  return 'bad';
}

function responseTone(mins: number | null): 'ok' | 'warn' | 'bad' {
  if (mins === null) return 'bad';
  if (mins <= 240) return 'ok';        // ≤4h
  if (mins <= 1440) return 'warn';     // ≤24h
  return 'bad';
}

function reviewTone(avg: number | null, count: number): 'ok' | 'warn' | 'bad' {
  if (avg === null || count < 3) return 'bad';
  if (avg >= 4.0) return 'ok';
  if (avg >= 3.0) return 'warn';
  return 'bad';
}

function activityTone(iso: string | null): 'ok' | 'warn' | 'bad' {
  if (!iso) return 'bad';
  const days = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (days <= 7) return 'ok';
  if (days <= 30) return 'warn';
  return 'bad';
}

function formatDuration(mins: number | null): string {
  if (mins === null) return '—';
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = mins / 60;
  if (h < 24) {
    const wholeH = Math.floor(h);
    const m = Math.round((h - wholeH) * 60);
    return m > 0 ? `${wholeH}h ${m}m` : `${wholeH}h`;
  }
  const d = h / 24;
  return `${d.toFixed(1)}d`;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'No activity';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `Inactive ${d}d`;
}

const TONE_BADGE: Record<'ok' | 'warn' | 'bad', string> = {
  ok: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  warn: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  bad: 'bg-destructive/15 text-destructive border-destructive/30',
};

export function AgencyScorecards() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<
    'leads' | 'conversion' | 'spend' | 'compliance' | 'name' | 'response' | 'review' | 'activity'
  >('leads');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: scorecards, isLoading } = useQuery({
    queryKey: ['agency-scorecards'],
    queryFn: async (): Promise<AgencyMetrics[]> => {
      const since30 = new Date(Date.now() - 30 * 86400000).toISOString();
      const since90 = new Date(Date.now() - 90 * 86400000).toISOString();

      const [agencies, properties, plots, projects, leads, featured, agentProfiles, leadNotes, reviews] = await Promise.all([
        supabase.from('agencies').select('id, name, slug, city, is_verified, logo_url, created_by'),
        supabase.from('properties').select('id, user_id, status, approval_status, created_at, title'),
        supabase.from('plots').select('id, user_id, status, approval_status, created_at, title'),
        supabase.from('projects').select('id, developer_id, approval_status, created_at'),
        supabase.from('leads').select('id, status, agent_id, created_at, property_id'),
        supabase.from('featured_listings').select('id, listing_id, listing_type, is_active, end_date, price_paid, created_by'),
        supabase.from('agent_profiles').select('id, user_id, agency_id, license_number, experience_years'),
        supabase.from('lead_notes').select('id, lead_id, created_at').gte('created_at', since90),
        supabase.from('reviews').select('id, agent_user_id, rating, status').eq('status', 'published'),
      ]);

      const ags = agencies.data || [];
      const props = properties.data || [];
      const plotRows = plots.data || [];
      const allLeads = leads.data || [];
      const feats = featured.data || [];
      const agents = agentProfiles.data || [];
      const notes = leadNotes.data || [];
      const allReviews = reviews.data || [];

      const ownerToAgency = new Map<string, string>();
      ags.forEach(a => { if (a.created_by) ownerToAgency.set(a.created_by, a.id); });
      const userToAgency = new Map<string, string>(ownerToAgency);
      agents.forEach(ag => { if (ag.user_id && ag.agency_id) userToAgency.set(ag.user_id, ag.agency_id); });

      // Earliest note per lead within window
      const firstNoteByLead = new Map<string, string>();
      notes.forEach(n => {
        const cur = firstNoteByLead.get(n.lead_id);
        if (!cur || n.created_at < cur) firstNoteByLead.set(n.lead_id, n.created_at);
      });

      // Agent user_id -> agency_id (for review aggregation)
      const agentUserToAgency = new Map<string, string>();
      agents.forEach(ag => { if (ag.user_id && ag.agency_id) agentUserToAgency.set(ag.user_id, ag.agency_id); });

      // Reviews bucketed per agency
      const reviewsByAgency = new Map<string, { sum: number; count: number }>();
      allReviews.forEach(r => {
        if (!r.agent_user_id || !r.rating) return;
        const aid = agentUserToAgency.get(r.agent_user_id);
        if (!aid) return;
        const cur = reviewsByAgency.get(aid) || { sum: 0, count: 0 };
        cur.sum += Number(r.rating);
        cur.count += 1;
        reviewsByAgency.set(aid, cur);
      });

      const out: AgencyMetrics[] = ags.map(a => {
        const isOwner = (uid: string | null) => !!uid && userToAgency.get(uid) === a.id;
        const myProps = props.filter(p => isOwner(p.user_id));
        const myPlots = plotRows.filter(p => isOwner(p.user_id));
        const propIds = new Set([...myProps.map(p => p.id), ...myPlots.map(p => p.id)]);

        const active = myProps.filter(p => p.status === 'active' && p.approval_status === 'approved').length
                     + myPlots.filter(p => p.status === 'active' && p.approval_status === 'approved').length;
        const draft = myProps.filter(p => p.status === 'draft').length
                    + myPlots.filter(p => p.status === 'draft').length;
        const pending = myProps.filter(p => p.approval_status === 'pending').length
                      + myPlots.filter(p => p.approval_status === 'pending').length;
        const rejected90d = myProps.filter(p => p.approval_status === 'rejected' && p.created_at >= since90).length
                          + myPlots.filter(p => p.approval_status === 'rejected' && p.created_at >= since90).length;

        const myLeads = allLeads.filter(l => l.property_id && propIds.has(l.property_id));
        const leads30d = myLeads.filter(l => l.created_at >= since30).length;
        const leads90d = myLeads.filter(l => l.created_at >= since90).length;
        const wonLeads90d = myLeads.filter(l => l.created_at >= since90 && l.status === 'converted').length;
        const conversionPct = leads90d > 0 ? Math.round((wonLeads90d / leads90d) * 1000) / 10 : 0;

        // Response time: leads in last 90d that received at least one note
        const myLeads90d = myLeads.filter(l => l.created_at >= since90);
        let totalResponseMs = 0;
        let answered = 0;
        myLeads90d.forEach(l => {
          const firstNote = firstNoteByLead.get(l.id);
          if (firstNote) {
            const diff = new Date(firstNote).getTime() - new Date(l.created_at).getTime();
            if (diff >= 0) {
              totalResponseMs += diff;
              answered += 1;
            }
          }
        });
        const avgResponseMinutes = answered > 0 ? Math.round(totalResponseMs / answered / 60000) : null;
        const unansweredCount = myLeads90d.length - answered;

        const myFeatured = feats.filter(f =>
          (f.listing_type === 'property' || f.listing_type === 'plot') && propIds.has(f.listing_id)
        );
        const featuredActive = myFeatured.filter(f => f.is_active && f.end_date >= new Date().toISOString().slice(0, 10)).length;
        const totalSpend = myFeatured.reduce((s, f) => s + (Number(f.price_paid) || 0), 0);

        const myAgents = agents.filter(ag => ag.agency_id === a.id);
        const agentsComplete = myAgents.length === 0
          ? true
          : myAgents.every(ag => !!ag.license_number && (ag.experience_years ?? 0) > 0);

        // Reviews
        const rev = reviewsByAgency.get(a.id);
        const reviewAvg = rev && rev.count > 0 ? Math.round((rev.sum / rev.count) * 10) / 10 : null;
        const reviewCount = rev?.count ?? 0;

        // Last activity: max over listings, leads, lead_notes
        let lastTs = 0;
        let lastType: AgencyMetrics['lastActivityType'] = null;
        [...myProps, ...myPlots].forEach(p => {
          const t = new Date(p.created_at).getTime();
          if (t > lastTs) { lastTs = t; lastType = 'listing'; }
        });
        myLeads.forEach(l => {
          const t = new Date(l.created_at).getTime();
          if (t > lastTs) { lastTs = t; lastType = 'lead'; }
        });
        const myLeadIds = new Set(myLeads.map(l => l.id));
        notes.forEach(n => {
          if (!myLeadIds.has(n.lead_id)) return;
          const t = new Date(n.created_at).getTime();
          if (t > lastTs) { lastTs = t; lastType = 'lead_note'; }
        });
        const lastActivityAt = lastTs > 0 ? new Date(lastTs).toISOString() : null;

        return {
          id: a.id,
          name: a.name,
          slug: a.slug,
          city: a.city,
          isVerified: !!a.is_verified,
          logoUrl: a.logo_url,
          ownerId: a.created_by,
          active, draft, pending, rejected90d,
          leads30d, leads90d, wonLeads90d, conversionPct,
          featuredActive, totalSpend,
          compliance: {
            logo: !!a.logo_url,
            verified: !!a.is_verified,
            agentsComplete,
          },
          avgResponseMinutes,
          responseSampleSize: answered,
          unansweredCount,
          reviewAvg,
          reviewCount,
          lastActivityAt,
          lastActivityType: lastType,
        };
      });
      return out;
    },
  });

  const filteredSorted = useMemo(() => {
    let list = (scorecards || []).slice();
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q) || (s.city || '').toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      switch (sortBy) {
        case 'name': return a.name.localeCompare(b.name);
        case 'conversion': return b.conversionPct - a.conversionPct;
        case 'spend': return b.totalSpend - a.totalSpend;
        case 'compliance': return complianceScore(b) - complianceScore(a);
        case 'response': {
          // null/no-data sinks to bottom; fastest (lowest minutes) first
          const av = a.avgResponseMinutes ?? Number.POSITIVE_INFINITY;
          const bv = b.avgResponseMinutes ?? Number.POSITIVE_INFINITY;
          return av - bv;
        }
        case 'review': {
          const av = a.reviewAvg ?? -1;
          const bv = b.reviewAvg ?? -1;
          return bv - av;
        }
        case 'activity': {
          const av = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
          const bv = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
          return bv - av;
        }
        case 'leads':
        default:
          return b.leads30d - a.leads30d;
      }
    });
    return list;
  }, [scorecards, search, sortBy]);

  const open = useMemo(() => filteredSorted.find(s => s.id === openId) || null, [filteredSorted, openId]);

  const exportCsv = () => {
    const rows = filteredSorted.map(s => ({
      name: s.name, city: s.city, verified: s.isVerified,
      active: s.active, draft: s.draft, pending: s.pending, rejected_90d: s.rejected90d,
      leads_30d: s.leads30d, leads_90d: s.leads90d, won_90d: s.wonLeads90d,
      conversion_pct: s.conversionPct, featured_active: s.featuredActive, total_spend_etb: s.totalSpend,
      compliance_score: complianceScore(s),
      avg_response_minutes: s.avgResponseMinutes ?? '',
      response_sample_size: s.responseSampleSize,
      unanswered_count: s.unansweredCount,
      review_avg: s.reviewAvg ?? '',
      review_count: s.reviewCount,
      last_activity_at: s.lastActivityAt ?? '',
    }));
    downloadCsv('agency_scorecards', rowsToCsv(rows));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agency Scorecards</h1>
          <p className="text-sm text-muted-foreground">Health snapshot of every agency on the platform.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by agency or city"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="leads">Sort: Lead volume (30d)</SelectItem>
              <SelectItem value="conversion">Sort: Conversion %</SelectItem>
              <SelectItem value="response">Sort: Response time</SelectItem>
              <SelectItem value="review">Sort: Review score</SelectItem>
              <SelectItem value="activity">Sort: Last activity</SelectItem>
              <SelectItem value="spend">Sort: Total spend</SelectItem>
              <SelectItem value="compliance">Sort: Compliance</SelectItem>
              <SelectItem value="name">Sort: Name (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-52" />)}
        </div>
      ) : filteredSorted.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">No agencies match your filters.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredSorted.map(s => {
            const compScore = complianceScore(s);
            const respT = responseTone(s.avgResponseMinutes);
            const revT = reviewTone(s.reviewAvg, s.reviewCount);
            const actT = activityTone(s.lastActivityAt);
            return (
              <Card
                key={s.id}
                className="cursor-pointer hover:border-primary/40 transition-colors"
                onClick={() => setOpenId(s.id)}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    {s.logoUrl ? (
                      <img src={s.logoUrl} alt="" className="h-10 w-10 rounded-md object-cover border" />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{s.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.city || '—'}</div>
                    </div>
                    {s.isVerified && <Badge variant="outline" className="text-[10px]">Verified</Badge>}
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center">
                    <Metric label="Active" value={s.active} tone={tone(s.active > 0 ? 1 : 0, 1)} />
                    <Metric label="Leads 30d" value={s.leads30d} tone={tone(s.leads30d > 5 ? 1 : s.leads30d > 0 ? 0.5 : 0, 1)} />
                    <Metric label="Conv %" value={`${s.conversionPct}`} tone={tone(s.conversionPct > 5 ? 1 : s.conversionPct > 0 ? 0.5 : 0, 1)} />
                    <Metric
                      label="Rating"
                      value={s.reviewAvg !== null ? `${s.reviewAvg}★` : '—'}
                      tone={revT}
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs gap-2 flex-wrap">
                    <Badge variant="outline" className={`${TONE_BADGE[respT]} text-[10px] gap-1`}>
                      <Clock className="h-3 w-3" /> Resp {formatDuration(s.avgResponseMinutes)}
                    </Badge>
                    <Badge variant="outline" className={`${TONE_BADGE[actT]} text-[10px] gap-1`}>
                      <Activity className="h-3 w-3" /> {relativeTime(s.lastActivityAt)}
                    </Badge>
                    <Badge variant="outline" className={`${TONE_BADGE[tone(compScore)]} text-[10px] gap-1`}>
                      <Shield className="h-3 w-3" /> {compScore}/3
                    </Badge>
                    <span className="text-muted-foreground text-[10px] ml-auto">
                      ETB {s.totalSpend.toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Sheet open={!!open} onOpenChange={(v) => !v && setOpenId(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {open && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" /> {open.name}
                </SheetTitle>
                <SheetDescription>{open.city || 'No city set'}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <Section title="Listings">
                  <KV k="Active" v={open.active} />
                  <KV k="Draft" v={open.draft} />
                  <KV k="Pending approval" v={open.pending} />
                  <KV k="Rejected (90d)" v={open.rejected90d} tone={open.rejected90d > 0 ? 'warn' : undefined} />
                </Section>

                <Section title="Leads & conversion">
                  <KV k="Leads (30d)" v={open.leads30d} />
                  <KV k="Leads (90d)" v={open.leads90d} />
                  <KV k="Won (90d)" v={open.wonLeads90d} />
                  <KV k="Conversion" v={`${open.conversionPct}%`} />
                </Section>

                <Section title="Engagement">
                  <KV
                    k="Avg response (90d)"
                    v={
                      <span className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 opacity-60" />
                        {formatDuration(open.avgResponseMinutes)}
                        <span className="text-xs text-muted-foreground font-normal">
                          {open.responseSampleSize > 0
                            ? `based on ${open.responseSampleSize} lead${open.responseSampleSize === 1 ? '' : 's'}`
                            : 'no answered leads'}
                        </span>
                      </span>
                    }
                    tone={responseTone(open.avgResponseMinutes) === 'bad' ? 'warn' : undefined}
                  />
                  {open.unansweredCount > 0 && (
                    <KV k="Unanswered leads (90d)" v={open.unansweredCount} tone="warn" />
                  )}
                  <KV
                    k="Review score"
                    v={
                      <span className="flex items-center gap-1.5">
                        <Star className="h-3.5 w-3.5 fill-current opacity-80" />
                        {open.reviewAvg !== null
                          ? `${open.reviewAvg} from ${open.reviewCount} review${open.reviewCount === 1 ? '' : 's'}`
                          : 'No approved reviews'}
                      </span>
                    }
                    tone={reviewTone(open.reviewAvg, open.reviewCount) === 'bad' ? 'warn' : undefined}
                  />
                  <KV
                    k="Last activity"
                    v={
                      <span className="flex items-center gap-1.5">
                        <Activity className="h-3.5 w-3.5 opacity-60" />
                        {relativeTime(open.lastActivityAt)}
                        {open.lastActivityType && (
                          <span className="text-xs text-muted-foreground font-normal">
                            — {open.lastActivityType.replace('_', ' ')}
                          </span>
                        )}
                      </span>
                    }
                    tone={activityTone(open.lastActivityAt) === 'bad' ? 'warn' : undefined}
                  />
                </Section>

                <Section title="Monetization">
                  <KV k="Active boosts" v={open.featuredActive} />
                  <KV k="Total spend" v={`ETB ${open.totalSpend.toLocaleString()}`} />
                </Section>

                <Section title="Compliance">
                  <KV k="Logo set" v={open.compliance.logo ? 'Yes' : 'No'} tone={open.compliance.logo ? undefined : 'warn'} />
                  <KV k="Verified" v={open.compliance.verified ? 'Yes' : 'No'} tone={open.compliance.verified ? undefined : 'warn'} />
                  <KV k="Agents profiles complete" v={open.compliance.agentsComplete ? 'Yes' : 'No'} tone={open.compliance.agentsComplete ? undefined : 'warn'} />
                </Section>

                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button size="sm" variant="outline" asChild>
                    <a href={open.slug ? `/agencies/${open.slug}` : `/agencies/${open.id}`} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open agency page
                    </a>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/dashboard/audit-log?entity_type=agency&entity_id=${open.id}`)}
                  >
                    <History className="h-3.5 w-3.5 mr-1.5" /> Audit log
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</div>
      <div className="border rounded-lg divide-y">{children}</div>
    </div>
  );
}

function KV({ k, v, tone }: { k: string; v: React.ReactNode; tone?: 'warn' }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <span className="text-muted-foreground shrink-0">{k}</span>
      <span className={`font-medium text-right ${tone === 'warn' ? 'text-destructive' : ''}`}>{v}</span>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: number | string; tone: 'ok' | 'warn' | 'bad' }) {
  return (
    <div className={`rounded-md border p-2 ${TONE_BADGE[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80 truncate">{label}</div>
      <div className="text-base font-bold truncate">{value}</div>
    </div>
  );
}
